import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** 수습평가서 — 레거시 appr_edit_forms_probationary_assessment */
export const PROBATION_EVALUATION_TYPE = 188;

export const isProbationEvaluationType = (apvType: number): boolean =>
  apvType === PROBATION_EVALUATION_TYPE;

export const PROBATION_TARGET_MISSING_MESSAGE =
  '수습 평가자의 아이디를 입력해주세요';
export const PROBATION_TARGET_NOT_FOUND_MESSAGE =
  '해당 유저가 존재하지 않습니다. 다시 아이디를 확인해주세요';

export const PROBATION_DEFAULT_SUBJECT = '수습 평가서';

/** 대상자 없으면 기본 문구만. 작성자 언어로 저장 */
export const buildProbationSubject = (t: TFunction, name?: string): string =>
  name
    ? `${t(PROBATION_DEFAULT_SUBJECT)} (${name})`
    : t(PROBATION_DEFAULT_SUBJECT);

/** GET /type/188/draft-source 응답 data. 대상자가 없으면 target·targetEmployeeNumber 가 null */
export type ProbationDraftSource = {
  code: number;
  attributes: { targetEmployeeNumber: string | null };
  display: {
    subject: string | null;
    target: {
      employeeNumber: string;
      name: string | null;
      organizationName: string | null;
      positionName: string | null;
      groupEnterDate: string | null;
    } | null;
  };
};

export type ProbationTarget = {
  name: string;
  positionName: string;
  organizationName: string;
  period: string;
};

/** 대상자 확인을 마친 상태. 저장 시 attributes 로 그대로 실림 */
export type ProbationEvaluationState = {
  /** 레거시 문서 등 사번이 없으면 null — 서버가 저장된 사번을 유지 */
  targetEmployeeNumber: string | null;
  /** 대상자 정보 표 HTML(레거시 #employee_text_area) */
  content: string;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 레거시 calculateDate — `입사일 ~ (입사일 + 3개월 - 1일)`, 월말 넘침도 JS Date 그대로 */
export const buildProbationPeriod = (enterDate: string | null): string => {
  const match = enterDate?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const [start, y, m, d] = match;
  const end = new Date(Number(y), Number(m) - 1, Number(d));
  end.setMonth(end.getMonth() + 3);
  end.setDate(end.getDate() - 1);
  return `${start} ~ ${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(
    end.getDate(),
  )}`;
};

export const toProbationTarget = (
  source: ProbationDraftSource | null | undefined,
): ProbationTarget | null => {
  const target = source?.display.target;
  if (!target) return null;
  return {
    name: target.name ?? '',
    positionName: target.positionName ?? '',
    organizationName: target.organizationName ?? '',
    period: buildProbationPeriod(target.groupEnterDate),
  };
};

/** 저장 attributes → 확인 완료 상태. content 가 없으면 대상자 미확인 */
export const parseProbationEvaluation = (
  attributes: unknown,
): ProbationEvaluationState | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const { content, targetEmployeeNumber } = attributes as Record<
    string,
    unknown
  >;
  if (typeof content !== 'string' || !content.trim()) return null;
  return {
    content,
    targetEmployeeNumber:
      typeof targetEmployeeNumber === 'string' && targetEmployeeNumber
        ? targetEmployeeNumber
        : null,
  };
};

export const checkProbationEvaluationBeforeSave = (
  state: ProbationEvaluationState | null | undefined,
): string | null => (state?.content ? null : PROBATION_TARGET_MISSING_MESSAGE);

export const buildProbationEvaluationAttributes = (
  state: ProbationEvaluationState | null | undefined,
): ApprovalAttributes | null =>
  state?.content
    ? {
        content: state.content,
        targetEmployeeNumber: state.targetEmployeeNumber,
      }
    : null;

export const probationEvaluationKey = (
  state: ProbationEvaluationState | null,
): string => state?.content ?? '';
